import { parseAndValidateDate } from '../utils/dateUtils'

export const API_TIMEOUTS = Object.freeze({
  DEFAULT: 8000,
  EXTENDED: 70000, // For heavy queries like all-devices-measurements
})

// Export for backward compatibility with hooks that expect DEFAULT_TIMEOUT
export const DEFAULT_TIMEOUT = API_TIMEOUTS.DEFAULT

// ES2022+ class for enhanced API error handling
class APIError extends Error {
  constructor(message, code, status) {
    super(message)
    this.name = 'APIError'
    this.code = code
    this.status = status
  }
}

// Optimized fetch wrapper with timeout and abort signal merging
const fetchWithTimeout = async (url, options = {}) => {
  const { timeout = API_TIMEOUTS.DEFAULT, signal, ...fetchOptions } = options
  
  // Create combined abort controller
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), timeout)
  
  // Efficiently handle signal merging
  signal?.addEventListener?.('abort', () => {
    clearTimeout(timeoutId)
    controller.abort()
  }, { once: true })
  
  if (signal?.aborted) {
    clearTimeout(timeoutId)
    throw new DOMException('Request was aborted', 'AbortError')
  }
  
  try {
    const response = await fetch(url, {
      ...fetchOptions,
      signal: controller.signal,
    })
    
    clearTimeout(timeoutId)
    
    if (!response.ok) {
      throw new APIError(
        `HTTP ${response.status}: ${response.statusText}`,
        'HTTP_ERROR',
        response.status,
      )
    }
    
    return response
  } catch (error) {
    clearTimeout(timeoutId)
    
    if (error.name === 'AbortError') {
      if (signal?.aborted) {
        throw error // Re-throw user cancellation
      }
      throw new APIError(
        'Request timed out. The server may be busy or there may be no data for your selection.',
        'TIMEOUT',
      )
    }
    
    throw error
  }
}

// Streamlined query string builder using modern JS
const buildQueryString = (params) => 
  new URLSearchParams(
    Object.entries(params).filter(([, value]) => value != null),
  ).toString()

// Enhanced response handler with proper content type detection
const handleResponse = async (response) => {
  const contentType = response.headers.get('content-type')
  
  if (contentType?.includes('application/json')) {
    const data = await response.json()
    return { data: Array.isArray(data) && data.length === 0 ? { empty: true, data: [] } : data }
  }
  
  if (contentType?.includes('application/octet-stream') || contentType?.includes('text/csv')) {
    return { data: await response.blob() }
  }
  
  return { data: await response.text() }
}

// Unified error handler to prevent re-renders
const handleAPIError = (error, emptyValue = []) => {
  if (error.name === 'AbortError') {
    return { data: emptyValue }
  }
  console.error('API Error:', error)
  return { data: emptyValue }
}

// DRY helper for date-based API calls
const createDateRangeQuery = (startTime, endTime, additionalParams = {}) => {
  const validStart = parseAndValidateDate(startTime)
  const validEnd = parseAndValidateDate(endTime)
  return buildQueryString({
    ...additionalParams,
    start: validStart.toISOString(),
    end: validEnd.toISOString(),
  })
}

// Streamlined API functions using shared patterns
export const fetchHistoricalData = async (device, measurement, startTime, endTime, abortSignal) => {
  try {
    const queryString = createDateRangeQuery(startTime, endTime, { device, measurement })
    const response = await fetchWithTimeout(`/api/historical?${queryString}`, { signal: abortSignal })
    return await handleResponse(response)
  } catch (error) {
    return handleAPIError(error)
  }
}

export const fetchCurrentData = async (abortSignal) => {
  try {
    const response = await fetchWithTimeout('/api', { signal: abortSignal })
    return await handleResponse(response)
  } catch (error) {
    return handleAPIError(error)
  }
}

export const fetchAllDeviceMeasurements = async (abortSignal) => {
  try {
    const response = await fetchWithTimeout('/api/all-devices-measurements', { 
      signal: abortSignal, 
      timeout: API_TIMEOUTS.EXTENDED,
    })
    return await handleResponse(response)
  } catch (error) {
    return handleAPIError(error)
  }
}

export const fetchHistoricalFaults = async (startTime, endTime, abortSignal) => {
  try {
    const queryString = createDateRangeQuery(startTime, endTime)
    const response = await fetchWithTimeout(`/api/historical-faults?${queryString}`, { signal: abortSignal })
    return await handleResponse(response)
  } catch (error) {
    return handleAPIError(error)
  }
}

export const fetchHistoricalConfigs = async (startTime, endTime, abortSignal) => {
  try {
    const queryString = createDateRangeQuery(startTime, endTime)
    const response = await fetchWithTimeout(`/api/historical-configs?${queryString}`, { signal: abortSignal })
    return await handleResponse(response)
  } catch (error) {
    return handleAPIError(error)
  }
}

export const exportDataAsCsv = async (devices, measurements, startTime, endTime, abortSignal) => {
  try {
    const devicesParam = Array.isArray(devices) ? devices.join(',') : ''
    const measurementsParam = Array.isArray(measurements) ? measurements.join(',') : ''
    
    const queryString = createDateRangeQuery(startTime, endTime, {
      devices: devicesParam,
      measurements: measurementsParam,
    })
    
    const response = await fetchWithTimeout(`/api/export-combined?${queryString}`, { signal: abortSignal })
    return await handleResponse(response)
  } catch (error) {
    if (error.name === 'AbortError') {
      throw new APIError('Export cancelled.', 'CANCELLED')
    }
    if (error instanceof APIError) {
      throw new APIError(`Export failed: ${error.message}`, 'EXPORT_FAILED', error.status)
    }
    throw error
  }
}
